<script setup>
import {defineProps, defineEmits, onMounted, ref, watchEffect, computed, watch} from "vue";
import { useI18n } from "vue-i18n";
import BasePagination from "@/components/tables/BasePagination.vue";
import Text from "@/components/common/Text.vue";
import ActionsButtons from "@/components/tables/ActionsButtons.vue";
import SkeletonLoader from "@/components/common/SkeletonLoader.vue";
import Select from "@/components/common/fields/Select.vue";
import { useDisplay } from "vuetify";
import {ColumnTable} from "@/enums/enums.js";
const { width } = useDisplay();
const isSmallScreen = computed(() => width.value < 1629);

const { t } = useI18n();
const columns = ref([]);

const props = defineProps({
    entity: Object,
    actions: Object,
    items: Array,
    loading: Boolean,
    totalItems: Object,
    currentPage: Number,
    itemsPerPage: Number,
    tooltipText: String,
    actionTooltipText: { type: String, default: "" },
    buttonCleanUrl: { type: Boolean, default: false },
    expandRowKey: { type: String, default: "id" },
    itemClass: { type: [String, Function, Object], default: null },
    tableToUse: { type: Object, default: null },
    fetchData: { type: Function, required: true },

    //PROPS (EXPAND)
    expandable: { type: Boolean, default: false },
    expandedKey: { type: String, default: null },
    expandedColumns: { type: Array, default: () => [] },
    expandedTitle: { type: String, default: "" }
});

const sortBy = ref(null);
const sortDesc = ref(false);
const emit = defineEmits(["edit", "delete", "page-change", "view", "duplicate", "onOrderBy"]);

const expandedRows = ref([]);

function getExpandRowId(row) {
    return row[props.expandRowKey];
}

function toggleExpand(id) {
    const index = expandedRows.value.indexOf(id);
    if (index > -1) expandedRows.value.splice(index, 1);
    else expandedRows.value.push(id);
}

function isExpanded(id) {
    return expandedRows.value.includes(id);
}

function getColumnConfig(col) {
    return ColumnTable[col.key] || ColumnTable.default;
}

function getColumnStyle(col) {
    const config = getColumnConfig(col);

    const width = isSmallScreen.value
        ? col.smallScreenWidth ?? config.smallScreenWidth
        : col.width ?? config.width;

    return {
        width,
        minWidth: width,
        maxWidth: col.maxWidth
    };
}

function resolveMaxChars(col) {
    const config = getColumnConfig(col);

    if (isSmallScreen.value) {
        return col.smallScreenMaxChars ?? config.smallScreenMaxChars;
    }

    return col.maxChars ?? config.maxChars;
}

function truncateText(text, col) {
    if (text === null || text === undefined) return "-";

    const maxChars = resolveMaxChars(col);
    const value = String(text);

    if (value.length <= maxChars) return value;
    return value.slice(0, maxChars) + "...";
}

function shouldShowTooltip(row, col) {
    if (col.key === "actions") return false;

    const text = row[col.key];
    if (!text) return false;

    return text.length > resolveMaxChars(col);
}

const selectedOrder = ref(null);

watch(selectedOrder, (newValue) => {
    if (newValue === null) {
        sortBy.value = null;
        triggerFetch();
    }
});

const orderableColumns = computed(() => {
    const cols = columns.value
        .filter(col => col.orderBy === true)
        .map(col => ({
            ...col,
            labelTranslated: t(col.label),
        }));

    return cols.length ? cols : null;
});

function handleSelectSort(key) {
    const col = columns.value.find(c => c.key === key);
    if (col) toggleSort(col);
}

function toggleDirection() {
    const col = columns.value.find(c => c.key === selectedOrder.value);
    if (col) toggleSort(col);
}

function onEdit(item) {
    emit("edit", item);
}

function onView(item) {
    emit("view", item);
}

function onDelete(item) {
    emit("delete", item);
}

function onDuplicate(item) {
    emit("duplicate", item);
}

watchEffect(() => {
    if (props.entity.tableColumns?.length) {
        columns.value = props.entity[props.tableToUse] ?? props.entity.tableColumns;
    }
});

function toggleSort(col) {
    if (col.orderBy === false) return;
    const columnKey = col.orderByKey || col.key || col.value;

    if (sortBy.value !== columnKey) {
        sortBy.value = columnKey;
        sortDesc.value = true;
    } else if (sortDesc.value) {
        sortDesc.value = false;
    } else {
        sortBy.value = null;
    }

    triggerFetch();
}

async function triggerFetch(params = {}) {
    const orderBy = sortBy.value
        ? { key: sortBy.value + (sortDesc.value ? " desc" : "") }
        : null;

    emit("onOrderBy", orderBy);

    await props.fetchData({
        page: params.page || props.currentPage
    });
}

function getRowClass(row) {
    const baseClass = { 'read-only-row': row.read_only };

    if (typeof props.itemClass === 'function') {
        return [baseClass, props.itemClass(row)];
    }

    if (typeof props.itemClass === 'string') {
        return [baseClass, props.itemClass];
    }

    if (typeof props.itemClass === 'object' && props.itemClass !== null) {
        return { ...baseClass, ...props.itemClass };
    }

    return baseClass;
}
</script>

<template>

    <div class="trezo-table-content">
        <div class="trezo-table">
            
            <div class="trezo-table-content">

                <!-- DESKTOP -->
                <div class="trezo-table d-none d-md-block custom-table">
                    
                    <v-table v-if="columns && columns.length">
                        
                        <thead>
                            
                        <tr>

                            <th v-for="col in columns" :key="col.key" :style="getColumnStyle(col)"  class="text-start" v-tooltip="isSmallScreen ? t(col.label) : false">
                               
                                {{ t(col.label) }}
                                
                                <v-icon
                                    v-if="col.orderBy !== false"
                                    size="small"
                                    class="ml-1 cursor-pointer"
                                    @click="toggleSort(col)"
                                >
                                
                                    <template v-if="sortBy === col.key">
                                        {{ sortDesc ? "mdi-arrow-down" : "mdi-arrow-up" }}
                                    </template>
                                    <template v-else>
                                        mdi-swap-vertical
                                    </template>
                                </v-icon>
                                
                            </th>
                            
                            <th style="width: 20px!important;" v-if="expandable"></th>
                        </tr>
                        </thead>

                        <tbody>
                        <template v-for="row in items" :key="row.id">

                            <!-- LINHA PRINCIPAL -->
                            <tr v-if="!loading" :class="getRowClass(row)">

                                <td v-for="col in columns" :key="col.key" v-tooltip="shouldShowTooltip(row, col) ? row[col.key] : false">
                                    <slot :name="col.key" v-if="col.key !== 'actions'" :row="row">  
                                        <router-link
                                            v-if="col.link"
                                            :to="col.link(row)"
                                            class="text-primary underline"
                                        >
                                            {{ truncateText(row[col.key], col) }}
                                        </router-link>

                                        <template v-else>
                                            {{ truncateText(row[col.key], col) }}
                                        </template>
                                    </slot>

                                    <ActionsButtons v-else
                                                    :actions="actions"
                                                    :row="row"
                                                    :duplicate="entity.customActions?.duplicate"
                                                    :tooltipText="tooltipText"
                                                    :actionTooltipText="actionTooltipText"
                                                    @on-view="onView"
                                                    @on-edit="onEdit"
                                                    @on-delete="onDelete"
                                                    @on-duplicate="onDuplicate"
                                    />                                   
                                </td>
                                <td v-if="expandable">
                                    {{ expandedTitle }}
                                    <v-btn v-if="row[expandedKey]?.length" icon variant="text" class="height-button-icon" @click="toggleExpand(getExpandRowId(row))">
                                        <v-icon >
                                            {{ isExpanded(getExpandRowId(row)) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <!-- EXPAND -->
                            <tr v-if="expandable && isExpanded(getExpandRowId(row))">
                                <td :colspan="columns.length + 1">
                                    <div class="pa-4rounded ">

                                        <v-table density="compact">
                                            <thead v-if="expandedColumns.length">
                                            <tr>
                                                <th v-for="col in expandedColumns" :key="col.key">
                                                    {{ col.label }}
                                                </th>
                                            </tr>
                                            </thead>

                                            <tbody v-if="row[expandedKey]?.length">
                                            <tr v-for="(child, i) in row[expandedKey]" :key="i">
                                                <td v-for="col in expandedColumns" :key="col.key"  v-tooltip="shouldShowTooltip(row, col) ? child[col.key] : false">
                                                    {{ truncateText(child[col.key], col) ?? '-' }}
                                                </td>
                                            </tr>
                                            </tbody>

                                            <tbody v-else>
                                            <tr>
                                                <td :colspan="expandedColumns.length || 1" class="text-center text-grey">
                                                    Sem dados disponíveis
                                                </td>
                                            </tr>
                                            </tbody>
                                        </v-table>

                                    </div>
                                </td>
                            </tr>

                        </template>
                        <v-btn
                            v-if="buttonCleanUrl && Object.keys($route.query).length"
                            variant="outlined"
                            @click="$router.replace({ query: {} })"
                            prepend-icon="mdi-arrow-left"
                            class="border rounded-lg px-4"
                        >
                            Voltar
                        </v-btn>
                        <!-- LOADING -->
                        <tr v-if="loading">
                            <td :colspan="columns.length + (expandable ? 1 : 0)" class="text-center py-4">
                                <SkeletonLoader page="list"/>
                            </td>
                        </tr>

                        <!-- EMPTY -->
                        <tr v-if="!loading && !items.length">
                            <td :colspan="columns.length + (expandable ? 1 : 0)" class="text-gray-500 italic py-4">
                                {{ $t("common.labels.noData") }}
                            </td>
                        </tr>

                        </tbody>
                    </v-table>
                </div>

                <!-- MOBILE -->
                <div class="d-block d-md-none">
                    <v-row dense>
                        <v-col v-for="row in items" :key="row.id" cols="12">
                            <v-card class="p-2 mb-3" elevation="1">
                                <v-card-text>

                                    <div v-for="col in columns" :key="col.key">
                                        <strong>{{ t(col.label) }}: </strong>
                                        <span>
                        <slot :name="col.key" v-if="col.key !== 'actions'" :row="row">
                            {{ row[col.key] }}
                        </slot>

                        <ActionsButtons v-else
                                        :actions="actions"
                                        :row="row"
                                        @on-view="onView"
                                        @on-edit="onEdit"
                                        @on-delete="onDelete"
                                        @on-duplicate="onDuplicate"
                        />
                    </span>
                                    </div>

                                    <!-- EXPAND MOBILE -->
                                    <div v-if="expandable" class="mt-2">
                                        <v-btn size="small" variant="text" @click="toggleExpand(row.id)">
                                            {{ isExpanded(row.id) ? 'Ocultar' : 'Ver itens' }}
                                        </v-btn>

                                        <div v-if="isExpanded(row.id)">
                                            <div v-if="row[expandedKey]?.length">
                                                <div v-for="(child, i) in row[expandedKey]" :key="i">
                                                    <div v-for="col in expandedColumns" :key="col.key">
                                                        <strong>{{ col.label }}:</strong> {{ child[col.key] ?? '-' }}
                                                    </div>
                                                    <v-divider class="my-2"/>
                                                </div>
                                            </div>

                                            <div v-else class="text-grey">
                                                Sem dados disponíveis
                                            </div>
                                        </div>
                                    </div>

                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <BasePagination
                    :fetch-data="triggerFetch"
                    :total-items="totalItems"
                    :current-page="currentPage"
                    :loading="loading"
                    @page-change="(page) => emit('page-change', page)"
                />

            </div>
        </div>
    </div>
</template>

<style scoped>

tr:hover {
    background-color: rgba(221, 221, 221, 0.25);
}

.custom-table {
    min-height: 630px;
}
.pagination-controls span {
    font-size: 14px;
}

.height-button-icon {
    height: 28px;
    width: 28px;
}

</style>
